import React, { useLayoutEffect, useRef } from 'react';
import { FiX } from 'react-icons/fi';
import NoteHeader from './NoteHeader.jsx';
import useNoteGesture from '../hooks/useNoteGesture.js';
import { colors, noteHeight } from '../util/notes.mjs';

const Note = function({note, layer, selected, onSelect, onChange, onDelete, onPaste}) {
  const {isDragging, getHandleProps} = useNoteGesture(note, onChange);

  const textRef = useRef(null);

  useLayoutEffect(() => {
    if (note.minimized) return;

    const textarea = textRef.current;

    let active = true;
    let previousWidth;

    const fitText = function() {
      if (!active) return;

      const content = textarea.parentElement;
      const scrollTop = content.scrollTop;

      // Reset the height so deleting text can shrink the editor as well.
      textarea.style.height = '0px';
      textarea.style.height = `${textarea.scrollHeight}px`;
      content.scrollTop = scrollTop;
    };

    fitText();

    const observer = new ResizeObserver(entries => {
      const width = entries[0].contentRect.width;

      // Height changes come from fitText itself; only width changes need a refit.
      if (width === previousWidth) return;

      previousWidth = width;
      fitText();
    });

    observer.observe(textarea);
    document.fonts?.ready.then(fitText);

    return () => {
      active = false;
      observer.disconnect();
    };
  }, [note.text, note.minimized]);

  const updateNote = function(patch) {
    onChange(note.id, patch);
  };

  const removeImage = function(imageId) {
    updateNote({
      images: note.images.filter(image => image.id !== imageId)
    });
  };

  const color = colors.find(item => item.id === note.color) || colors[0];

  return <article
    className={`note v ${note.minimized ? 'isMinimized' : ''} ${selected ? 'isSelected' : ''} ${isDragging ? 'isDragging' : ''}`}
    data-note-id={note.id}
    aria-label={note.title || `${color.name} sticky note`}
    onPointerDownCapture={() => onSelect(note.id)}
    onFocusCapture={() => onSelect(note.id)}
    style={{
      '--note-color': `var(--note-${color.id}, ${color.value})`,
      translate: `${note.x}px ${note.y}px`,
      width: note.width,
      height: noteHeight(note),
      zIndex: layer
    }}
  >
      <NoteHeader
        moveHandleProps={getHandleProps('move')}
        title={note.title || ''}
        onTitleChange={title => updateNote({
          title
        })}
        minimized={Boolean(note.minimized)}
        onToggleMinimized={() => updateNote({minimized: !note.minimized})}
        selected={selected}
        color={note.color}
        onColorChange={color => updateNote({
          color
        })}
        onPaste={() => onPaste(note.id)}
        onDelete={() => onDelete(note.id)}
      />

      <div className='noteContent v'>
        <textarea
          ref={textRef}
          aria-label='note text'
          placeholder='something on your mind?'
          value={note.text}
          spellCheck
          onChange={event => updateNote({
            text: event.target.value
          })}
        />
        {note.images.map(image => <figure className='noteImage' key={image.id}>
            <img src={image.src} alt={image.name} draggable={false} />
            <button
              className='iconButton removeImage'
              aria-label={`remove ${image.name}`}
              title='remove image'
              onClick={() => removeImage(image.id)}
            >
              <FiX size={15} aria-hidden='true' />
            </button>
          </figure>)}
      </div>

      {!note.minimized && <button
        className='resizeHandle'
        aria-label='resize note; use arrow keys when focused'
        title='drag to resize · arrow keys to adjust'
        {...getHandleProps('resize')}
      />}
    </article>;
};

export default Note;
